import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@mantine/core';
import { IoSearchOutline } from 'react-icons/io5';
import { SELLER_ME_QUERY_KEY, sellerOnboardingApi } from '@modules/seller-onboarding/api';
import { usePickupPoints } from '@modules/pickup-points';
import { ordersApi } from '@modules/orders/api/orders.api';
import { useOrdersByPickupPoint, ORDERS_PAGE_SIZE, type Order, OrderStatus, getStatusLabel, formatTimeSlot, isSlotStartWithinTwoHours } from '@modules/orders';
import { OrderStatus as OrderStatusTag } from '@modules/orders/components';
import { DeliveryStatusTag } from '@modules/delivery';
import { Breadcrumbs, OptionsTable, type OptionsTableColumn } from '@components';
import { RefetchButton, Input, PillGroup, Button, Pagination, QuietSelect } from '@shared/ui';
import { useDebounce } from '@shared/hooks';
import { formatDate, formatPrice } from '@shared/utils';
import { ORDERS_PICKUP_POINT_STORAGE_KEY } from '@shared/utils/clear-seller-client-state';
import '@shared/styles/page.css';
import './OrdersPage.css';

function renderOrderDelivery(order: Order) {
  if (order.deliveryMethod !== 'DELIVERY') return 'Самовывоз';
  if (order.pickupPointData?.deliveryProvider === 'SELLER') return 'Своими силами';
  return (
    <span className="orders-page__delivery-cell">
      <DeliveryStatusTag status={order.dostavistaDeliveryStatus} />
      {order.dostavistaReturnInProgress ? (
        <span className="orders-page__return-tag">Идёт возврат</span>
      ) : null}
    </span>
  );
}

function getSellerAcceptanceText(order: Order): string | null {
  if (
    order.status !== OrderStatus.CONFIRMED ||
    !order.sellerAcceptanceDeadline
  ) {
    return null;
  }
  return `до ${formatDate(
    order.sellerAcceptanceDeadline,
    order.pickupPointData?.city,
  )}`;
}

function getStoredPickupPointId(): number | null {
  try {
    const stored = localStorage.getItem(ORDERS_PICKUP_POINT_STORAGE_KEY);
    if (stored == null) return null;
    const num = parseInt(stored, 10);
    return Number.isNaN(num) ? null : num;
  } catch {
    return null;
  }
}

function getSenderDisplay(order: Order): string {
  const name = [order.user?.name, order.user?.surname].filter(Boolean).join(' ').trim();
  const phone = order.user?.phone ?? '';
  if (!name && !phone) return '—';
  return phone ? `${name || ''} ${phone}` : name || '—';
}

export default function OrdersPage() {
  const navigate = useNavigate();
  const { data: sellerApp } = useQuery({
    queryKey: SELLER_ME_QUERY_KEY,
    queryFn: () => sellerOnboardingApi.getMe(),
  });
  const pickupPointsQuery = usePickupPoints();
  const allowedPickupPointIds =
    sellerApp?.cabinetRole === 'STAFF' ? sellerApp.staffPickupPointIds ?? [] : null;
  const pickupPoints = (pickupPointsQuery.data ?? []).filter((p) =>
    allowedPickupPointIds == null ? true : allowedPickupPointIds.includes(p.id),
  );
  const pickupPointsLoading = pickupPointsQuery.isLoading;
  const pickupPointsError = pickupPointsQuery.error;
  const pickupPointFetching = pickupPointsQuery.isFetching;
  const refetch = pickupPointsQuery.refetch;
  const [selectedPickupPointId, setSelectedPickupPointId] = useState<number | null>(getStoredPickupPointId);
  const [activeStatusTab, setActiveStatusTab] = useState<OrderStatus>(OrderStatus.CONFIRMED);
  const [searchQuery, setSearchQuery] = useState('');
  const orderColumns: OptionsTableColumn<Order>[] = [
    {
      key: 'orderNumber',
      label: 'Номер заказа',
      render: (order: Order) => (
        <div>
          <div className="orders-page__order-number">{order.orderNumber}</div>
          {order.dostavistaCreationError ? (
            <div className="orders-page__delivery-error">Не удалось создать доставку</div>
          ) : null}
        </div>
      ),
    },
    ...(activeStatusTab === OrderStatus.CONFIRMED
      ? [
          {
            key: 'sellerAcceptanceDeadline',
            label: 'Принять заказ',
            render: (order: Order) => {
              const text = getSellerAcceptanceText(order);
              return text ? (
                <span className="orders-page__acceptance-deadline">{text}</span>
              ) : (
                '—'
              );
            },
          },
        ]
      : []),
    {
      key: 'sender',
      label: 'Клиент',
      render: (order: Order) => getSenderDisplay(order),
    },
    {
      key: 'timeSlot',
      label: 'Слот времени',
      render: (order: Order) => {
        const text = formatTimeSlot(order.assemblySlotData);
        const isUrgent = isSlotStartWithinTwoHours(
          order.assemblySlotData,
          order.pickupPointData?.timezone,
        );
        return isUrgent ? <span className="orders-page__slot--urgent">{text}</span> : text;
      },
    },
    {
      key: 'dostavistaCourierPickupArrival',
      label: 'Курьер',
      render: (order: Order) =>
        order.deliveryMethod === 'DELIVERY' ? order.dostavistaCourierPickupArrival ?? '—' : '—',
    },
    {
      key: 'totalAmount',
      label: 'Сумма',
      render: (order: Order) => (
        <span className="orders-page__amount">
          {formatPrice(order.totalAmount - (order.deliveryAmount ?? 0))}
        </span>
      ),
    },
    {
      key: 'delivery',
      label: 'Доставка',
      render: (order: Order) => renderOrderDelivery(order),
    },
    {
      key: 'status',
      label: 'Статус',
      render: (order: Order) => <OrderStatusTag status={order.status} />,
    },
  ];

  // Порядок статусов для табов
  const statusOrder: OrderStatus[] = [
    OrderStatus.PENDING,    // Ожидает оплаты
    OrderStatus.CONFIRMED,  // Подтвержден
    OrderStatus.IN_PROGRESS, // В работе
    OrderStatus.READY,       // Готов
    OrderStatus.IN_DELIVERY, // В доставке
    OrderStatus.DELIVERED,   // Доставлен
    OrderStatus.CANCELED,    // Отменен
  ];

  /** Табы, на которых показываем счётчик (не «Доставлен» и не «Отменён»). */
  const statusTabWithBadge: OrderStatus[] = [
    OrderStatus.PENDING,
    OrderStatus.CONFIRMED,
    OrderStatus.IN_PROGRESS,
    OrderStatus.READY,
    OrderStatus.IN_DELIVERY,
  ];

  // Восстанавливаем из localStorage или выбираем первую точку; сбрасываем id, которого уже нет в списке
  useEffect(() => {
    if (pickupPoints.length === 0) return;
    const storedId = getStoredPickupPointId();
    const isValidStored = storedId != null && pickupPoints.some((p) => p.id === storedId);
    const isValidSelected =
      selectedPickupPointId != null && pickupPoints.some((p) => p.id === selectedPickupPointId);

    if (isValidSelected) return;

    if (isValidStored) {
      setSelectedPickupPointId(storedId);
      return;
    }
    const fallbackId = pickupPoints[0].id;
    setSelectedPickupPointId(fallbackId);
    localStorage.setItem(ORDERS_PICKUP_POINT_STORAGE_KEY, String(fallbackId));
  }, [pickupPoints, selectedPickupPointId]);

  const getContent = () => {
    if (pickupPointsLoading || pickupPointFetching) {
      return (
        <div className="empty-container">
          <Loader />
        </div>
      );
    }


    if (pickupPointsError && refetch) {
      return (
        <RefetchButton
          refetch={refetch}
          isLoading={pickupPointsLoading || pickupPointFetching}
        />
      );
    }

    if (pickupPoints.length === 0) {
      return (
        <div className="orders-page-empty" role="status">
          <p className="orders-page-empty__title">Нет точек самовывоза</p>
          <p className="orders-page-empty__text">
            Чтобы принимать заказы, добавьте точку самовывоза — без неё покупатели не смогут
            оформить заказ у вас.
          </p>
          <Button type="button" onClick={() => navigate('/contacts')}>
            К точкам самовывоза
          </Button>
        </div>
      );
    }

    return null;
  };

  const selectedPickupPoint =
    selectedPickupPointId != null
      ? pickupPoints.find((p) => p.id === selectedPickupPointId)
      : undefined;

  const { data: statusCounts = {} } = useQuery({
    queryKey: ['orders', 'pickup-point', selectedPickupPointId, 'status-counts'],
    queryFn: () => ordersApi.getPickupPointStatusCounts(selectedPickupPointId!),
    enabled:
      selectedPickupPointId != null && !pickupPointsLoading && pickupPoints.length > 0,
  });

  const { data: activeOrdersPickupPoints } = useQuery({
    queryKey: ['orders', 'seller', 'pickup-points-with-active-orders'],
    queryFn: () => ordersApi.getPickupPointsWithActiveOrders(),
    enabled: !pickupPointsLoading && pickupPoints.length > 0,
    refetchInterval: 60_000,
  });

  const activeOrdersPickupPointIds = useMemo(
    () => new Set(activeOrdersPickupPoints?.pickupPointIds ?? []),
    [activeOrdersPickupPoints],
  );

  const showOtherPickupPointDot =
    pickupPoints.length > 1 &&
    selectedPickupPointId != null &&
    [...activeOrdersPickupPointIds].some((id) => id !== selectedPickupPointId);

  const contentState = getContent();

  return (
    <div className="page-container orders-page">
      <Breadcrumbs items={[
        { label: 'Главная', path: '/' },
        { label: 'Заказы' }
      ]} />

      {!pickupPointsLoading && pickupPoints.length > 0 ? (
        <div className="orders-page__filters">
          <div className="orders-page__search">
            <Input
              className="orders-page__search-input"
              type="text"
              placeholder="Поиск по номеру заказа или клиенту..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftAddon={<IoSearchOutline size={16} aria-hidden />}
            />
          </div>

          <div className="orders-page__filters-controls">
            <QuietSelect
              className="orders-page__select-wrap"
              ariaLabel="Выбрать точку самовывоза"
              label={
                selectedPickupPoint
                  ? `${selectedPickupPoint.city} - ${selectedPickupPoint.address}`
                  : 'Точка самовывоза'
              }
              value={selectedPickupPointId}
              triggerMarkerTitle={
                showOtherPickupPointDot
                  ? 'Есть активные заказы на другой точке самовывоза'
                  : undefined
              }
              onChange={(pointId) => {
                setSelectedPickupPointId(pointId);
                localStorage.setItem(ORDERS_PICKUP_POINT_STORAGE_KEY, String(pointId));
              }}
              options={pickupPoints.map((point) => ({
                value: point.id,
                label: `${point.city} - ${point.address}`,
                markerTitle: activeOrdersPickupPointIds.has(point.id)
                  ? 'Есть активные заказы'
                  : undefined,
              }))}
            />
          </div>
        </div>
      ) : null}

      {!contentState && selectedPickupPoint ? (
        <PillGroup
          className="orders-page__status-pills"
          ariaLabel="Статус заказа"
          options={statusOrder.map((status) => {
            const n = statusCounts[status] ?? 0;
            const showBadge = statusTabWithBadge.includes(status) && n > 0;
            return {
              value: status,
              label: (
                <span className="orders-page__tab-label">
                  {getStatusLabel(status)}
                  {showBadge ? (
                    <span className="orders-page__tab-badge" title={`${n} заказ(ов)`}>
                      {n > 99 ? '99+' : n}
                    </span>
                  ) : null}
                </span>
              ),
            };
          })}
          value={activeStatusTab}
          onChange={setActiveStatusTab}
        />
      ) : null}

      <div className="content-wrapper">
        {contentState ? (
          contentState
        ) : selectedPickupPoint ? (
          <div className="orders-page__tabs-panel" role="tabpanel">
            <PickupPointOrders
              pickupPoint={selectedPickupPoint}
              columns={orderColumns}
              statusFilter={activeStatusTab}
              searchQuery={searchQuery.trim()}
            />
          </div>
        ) : (
          <div className="empty-container">
            <Loader />
          </div>
        )}
      </div>
    </div>
  );
}

interface PickupPointOrdersProps {
  pickupPoint: {
    id: number;
    city: string;
    address: string;
  };
  columns: OptionsTableColumn<Order>[];
  statusFilter?: OrderStatus | null;
  searchQuery?: string;
}

function filterOrdersBySearch(orders: Order[], query: string): Order[] {
  if (!query) return orders;
  const q = query.toLowerCase().replace(/\s/g, '');
  return orders.filter((order) => {
    const orderNumber = (order.orderNumber ?? '').toLowerCase().replace(/\s/g, '');
    const phone = (order.user?.phone ?? '').replace(/\D/g, '');
    const phoneQuery = query.replace(/\D/g, '');
    return orderNumber.includes(q) || (phoneQuery && phone.includes(phoneQuery));
  });
}

const PAGINATED_STATUSES: OrderStatus[] = [OrderStatus.DELIVERED, OrderStatus.CANCELED];

function PickupPointOrders({ pickupPoint, columns, statusFilter, searchQuery = '' }: PickupPointOrdersProps) {
  const isPaginatedTab = statusFilter != null && PAGINATED_STATUSES.includes(statusFilter);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [page, setPage] = useState(1);
  const filterKey = `${pickupPoint.id}:${statusFilter ?? ''}:${isPaginatedTab ? debouncedSearchQuery : ''}`;
  const [pageFilterKey, setPageFilterKey] = useState(filterKey);
  if (pageFilterKey !== filterKey) {
    setPageFilterKey(filterKey);
    setPage(1);
  }
  const currentPage = pageFilterKey === filterKey ? page : 1;
  const {
    data: orders = [],
    total = 0,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useOrdersByPickupPoint(
    pickupPoint.id,
    statusFilter ?? undefined,
    isPaginatedTab ? debouncedSearchQuery : searchQuery,
    isPaginatedTab ? currentPage : 1,
  );
  const navigate = useNavigate();
  const filteredOrders = isPaginatedTab ? orders : filterOrdersBySearch(orders, searchQuery);

  const emptyMessage = statusFilter
    ? `Нет заказов со статусом «${getStatusLabel(statusFilter)}» для этой точки самовывоза`
    : 'Нет заказов для этой точки самовывоза';
  const listLoading = isLoading || isFetching;

  const openOrder = (order: Order) => {
    navigate(`/orders/${order.id}`);
  };

  return (
    <div className="orders-table-scroll">
      <div className="orders-table-scroll__body">
        <div className="orders-page__desktop-table">
          <OptionsTable
            columns={columns}
            data={filteredOrders}
            loading={listLoading}
            error={error}
            refetch={refetch}
            emptyMessage={emptyMessage}
            onRowClick={openOrder}
          />
        </div>

        <div className="orders-page__mobile-list" aria-label="Список заказов">
          {error && refetch ? (
            <RefetchButton
              refetch={refetch}
              isLoading={listLoading}
              errorMessage={error instanceof Error ? error.message : 'Не удалось загрузить данные'}
            />
          ) : listLoading ? (
            <div className="empty-container">
              <Loader />
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="orders-page__empty" role="status">
              {emptyMessage}
            </div>
          ) : (
            filteredOrders.map((order) => {
              const slotText = formatTimeSlot(order.assemblySlotData);
              const isUrgent = isSlotStartWithinTwoHours(
          order.assemblySlotData,
          order.pickupPointData?.timezone,
        );
              return (
                <button
                  key={order.id}
                  type="button"
                  className="orders-page__mobile-card"
                  onClick={() => openOrder(order)}
                >
                  <div className="orders-page__mobile-card-top">
                    <div className="orders-page__mobile-card-id">
                      <span className="orders-page__order-number">{order.orderNumber}</span>
                      {order.dostavistaCreationError ? (
                        <span className="orders-page__delivery-error">Не удалось создать доставку</span>
                      ) : null}
                    </div>
                    <span className="orders-page__amount">
                      {formatPrice(order.totalAmount - (order.deliveryAmount ?? 0))}
                    </span>
                  </div>
                  <div className="orders-page__mobile-card-meta">
                    <OrderStatusTag status={order.status} />
                    <span className="orders-page__mobile-card-delivery">
                      {renderOrderDelivery(order)}
                    </span>
                  </div>
                  {getSellerAcceptanceText(order) ? (
                    <div className="orders-page__acceptance-deadline">
                      {getSellerAcceptanceText(order)}
                    </div>
                  ) : null}
                  <div className="orders-page__mobile-card-row">
                    <span className="orders-page__mobile-card-label">Клиент</span>
                    <span className="orders-page__mobile-card-value">{getSenderDisplay(order)}</span>
                  </div>
                  <div className="orders-page__mobile-card-row">
                    <span className="orders-page__mobile-card-label">Слот</span>
                    <span
                      className={`orders-page__mobile-card-value${isUrgent ? ' orders-page__slot--urgent' : ''}`}
                    >
                      {slotText}
                    </span>
                  </div>
                  {order.deliveryMethod === 'DELIVERY' && order.dostavistaCourierPickupArrival ? (
                    <div className="orders-page__mobile-card-row">
                      <span className="orders-page__mobile-card-label">Курьер</span>
                      <span className="orders-page__mobile-card-value">
                        {order.dostavistaCourierPickupArrival}
                      </span>
                    </div>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      </div>
      {isPaginatedTab && (
        <Pagination
          page={currentPage}
          onChange={setPage}
          totalItems={total}
          pageSize={ORDERS_PAGE_SIZE}
        />
      )}
    </div>
  );
}
